import { RefreshCcw, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useWebshopStatus } from "@/lib/useWebshopStatus";
import { formatDateTime } from "@/lib/format";

const STATE_LABEL = {
  ok: "Verbonden", syncing: "Bezig met bijwerken", error: "Synchronisatiefout",
  not_configured: "Niet ingesteld", pending: "Wacht op eerste synchronisatie",
};

function Row({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between border-b border-border last:border-0 pb-2 gap-4" data-testid={testId}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function Configured({ ok }) {
  return ok ? "Ingesteld" : "Niet ingesteld";
}

export function WebshopPanel() {
  const { status, syncing, syncNow } = useWebshopStatus();
  if (!status) return null;
  const cfg = status.config || {};
  const orders = status.open_orders || {};

  const onSync = async () => {
    try {
      const data = await syncNow();
      if (data.state === "error") toast.error("Bijwerken mislukt", { description: data.error_message });
      else toast.success("Webshop-gegevens bijgewerkt");
    } catch {
      toast.error("Bijwerken mislukt", { description: "Koppeling is nog niet ingesteld." });
    }
  };

  return (
    <div className="space-y-6" data-testid="webshop-panel-content">
      <div className="surface p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="p-3 rounded-md bg-primary/10 text-primary w-fit"><ShoppingBag className="w-6 h-6" /></div>
          <div className="flex-1">
            <div className="label-eyebrow">WooCommerce · kipkruiden.nl</div>
            <h2 className="text-xl md:text-2xl font-semibold mt-1" data-testid="webshop-state">{STATE_LABEL[status.state] || status.state}</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Producten, voorraad en open bestellingen worden elke {status.interval_minutes} minuten automatisch opgehaald.
              Het dashboard leest alleen; er wordt niets in de webshop aangepast.
            </p>
            {status.error_message && <p className="mt-2 text-status-critical text-sm" data-testid="webshop-error">{status.error_message}</p>}
          </div>
          <Button onClick={onSync} disabled={syncing || !cfg.fully_configured} className="touch-tap" data-testid="webshop-sync-now">
            <RefreshCcw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> Nu bijwerken
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="surface p-6 space-y-2 text-sm">
          <h3 className="text-lg font-semibold mb-4">Laatste synchronisatie</h3>
          <Row label="Laatst gelukt" value={status.last_success ? formatDateTime(status.last_success) : "Nog niet"} testId="webshop-last-success" />
          <Row label="Producten" value={status.product_count ?? "—"} testId="webshop-product-count" />
          <Row label="Open webshop-bestellingen" value={status.open_orders_total ?? "—"} testId="webshop-orders-total" />
          <Row label="In behandeling" value={orders.processing ?? "—"} />
          <Row label="In de wacht" value={orders["on-hold"] ?? "—"} />
          <Row label="Wacht op betaling" value={orders.pending ?? "—"} />
        </div>
        <div className="surface p-6 space-y-2 text-sm" data-testid="webshop-config">
          <h3 className="text-lg font-semibold mb-4">Server-side configuratie</h3>
          <Row label="WC_BASE_URL" value={cfg.base_url || "Niet ingesteld"} />
          <Row label="WC_CONSUMER_KEY" value={<Configured ok={cfg.consumer_key_configured} />} testId="webshop-config-key" />
          <Row label="WC_CONSUMER_SECRET" value={<Configured ok={cfg.consumer_secret_configured} />} testId="webshop-config-secret" />
          <Row label="xCore-veld Magazijn 1 (Hoofdmagazijn)" value={cfg.xcore_fields?.kipkruiden || "Nog niet ingesteld — berekend (totaal min Zeewolde)"} testId="webshop-config-xcore-kk" />
          <Row label="xCore-veld Magazijn 2 (Zeewolde)" value={cfg.xcore_fields?.vandenbrink || "Nog niet ingesteld — handmatige invoer"} testId="webshop-config-xcore-vdb" />
        </div>
      </div>
    </div>
  );
}
